import { usePaymentFlags } from '@/feature/devsettings/hooks/use-payment-flags-store';
import { useJurisdictionStore } from '@/hooks/use-jurisdiction';
import { isAndroid, isWeb } from '@/utils/constants-platform-specific';

/**
 * Custom hook that returns whether or not a payment method is in enabled/disabled state.
 * Combines debug flags with Prismic jurisdiction settings for all payment providers.
 *
 * @returns {Object} Object containing payment method enablement flags
 * @returns {boolean} isDebugMode - True if debug mode is enabled
 * @returns {boolean} enableNuvei - True if Nuvei payment provider is enabled
 * @returns {boolean} enablePayByBank - True if Pay By Bank (Paysafe ACH) deposits are enabled
 * @returns {boolean} enablePayPal - True if PayPal deposits/withdrawals are enabled
 * @returns {boolean} enableVenmo - True if Venmo (Paysafe) deposits are enabled
 * @returns {boolean} enableInstantBankTransfers - True if Instant Bank Transfers (Mazooma/Nuvei) are enabled
 * @returns {boolean} enableDebitCard - True if debit card deposits are enabled
 * @returns {boolean} isAddDebitCardAndDepositEnabled - True if debit card deposits should be shown (combines enableDebitCard with Android-specific restrictions)
 * @returns {boolean} enableTrustly - True if Trustly deposits are enabled
 * @returns {boolean} enableTrustlyDepositForManualKycUsers - True if Trustly is enabled for manual KYC users
 * @returns {boolean} enableWithdrawals - True if withdrawals are enabled
 */
export const usePaymentMethodEnabled = () => {
    const { flags } = usePaymentFlags();
    const jurisdictionSettings = useJurisdictionStore(state => state.jurisdictionSettings);

    const globalSettings = useJurisdictionStore(store => store.jurisdictionSettings?.globalSettings?.featureFlags);
    const onlyTrustlyMethodEnabled = globalSettings?.enable_only_trustly_payment_method?.enabled ?? false;

    const isDebugMode = flags.isDebugMode.enabled;

    const enableTrustly = isDebugMode
        ? flags.enableTrustlyDeposit.enabled
        : jurisdictionSettings?.productConfig?.settings?.enable_trustly_deposit_method ?? false;

    const enableTrustlyDepositForManualKycUsers = isDebugMode
        ? flags.enableTrustlyDepositForManualKycUsers.enabled
        : jurisdictionSettings?.productConfig?.settings?.enable_trustly_for_manually_kyc_users ?? false;

    // Withdrawals enabled flag from Prismic product settings
    const enableWithdrawals = jurisdictionSettings?.productConfig?.settings?.withdraw_enabled ?? false;

    // If enable_only_trustly_payment_method is enabled, all other payment methods are disabled
    if (onlyTrustlyMethodEnabled) {
        return {
            isDebugMode,
            enableNuvei: false,
            enablePayByBank: false,
            enableWithdrawals,
            enablePayPal: false,
            enableVenmo: false,
            enableInstantBankTransfers: false,
            enableDebitCard: false,
            isAddDebitCardAndDepositEnabled: false,
            enableTrustly,
            enableTrustlyDepositForManualKycUsers,
        };
    }

    const enableNuvei = isDebugMode
        ? flags.nuveiEnabled.enabled
        : jurisdictionSettings?.globalSettings?.featureFlags?.enable_nuvei?.enabled ?? false;

    // Enable Pay By Bank (Paysafe) for jurisdictions that have it enabled
    const enablePayByBank = isDebugMode
        ? flags.enablePayByBank?.enabled
        : jurisdictionSettings?.productConfig?.settings?.deposit_pay_by_bank_enabled ?? false;

    // Enable PayPal for jurisdictions that have it enabled
    const enablePayPal = isDebugMode
        ? flags.enablePaypal?.enabled
        : jurisdictionSettings?.productConfig?.settings?.deposit_paypal_enabled ?? false;

    // Enable Venmo (Paysafe) for jurisdictions that have it enabled
    const enableVenmo = isDebugMode
        ? flags.enableVenmo?.enabled
        : jurisdictionSettings?.productConfig?.settings?.deposit_venmo_enabled ?? false;

    // Mazooma (Nuvei) is the same as IBT (Paysafe) in this context
    const enableInstantBankTransfers = isDebugMode
        ? flags.enableInstantBankTransfers.enabled
        : jurisdictionSettings?.productConfig?.settings?.deposit_instant_bank_transfers_enabled ?? false;

    const enableDebitCardMobile = isDebugMode
        ? flags.enableDebitCardDeposit.enabled
        : jurisdictionSettings?.productConfig?.settings?.deposit_debit_card_enabled ?? false;
    const enableDebitCardWeb =
        jurisdictionSettings?.globalSettings?.featureFlags?.enable_payments_debit_card_web?.enabled;
    const enableDebitCard = isWeb ? enableDebitCardWeb ?? enableDebitCardMobile : enableDebitCardMobile;

    // On Android we only want to display a certain set of payment methods, see https://pitchtech.atlassian.net/browse/CSP-1884
    const isAddDebitCardDisabled = isAndroid
        ? globalSettings?.enable_only_trustly_and_saved_debit_card_android?.enabled ?? false
        : false;
    const isAddDebitCardAndDepositEnabled = enableDebitCard && !isAddDebitCardDisabled;
    // CSP-1884 - On Android only saved debit cards are shown; users cannot add new ones.
    const isOnlyTrustlyAndSavedDebitOnAndroid = enableDebitCard && !isAddDebitCardAndDepositEnabled;

    return {
        isDebugMode,
        enableNuvei,
        enablePayByBank,
        enableWithdrawals,
        enablePayPal,
        enableVenmo,
        enableInstantBankTransfers,
        enableDebitCard,
        isAddDebitCardAndDepositEnabled,
        isOnlyTrustlyAndSavedDebitOnAndroid,
        enableTrustly,
        enableTrustlyDepositForManualKycUsers,
    };
};
